import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { MessageCircle, LogOut, LayoutDashboard, Users, Settings, Table2, UserCircle } from "lucide-react";
import AdminDashboard from "@/components/dashboard/AdminDashboard";
import LeadsTable from "@/components/dashboard/LeadsTable";
import SellersManager from "@/components/dashboard/SellersManager";
import SettingsPanel from "@/components/dashboard/SettingsPanel";
import SellerView from "@/components/dashboard/SellerView";

const ADMIN_TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "leads", label: "CRM Leads", icon: Table2 },
  { id: "sellers", label: "Vendedores", icon: Users },
  { id: "settings", label: "Configuración", icon: Settings },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState(isAdmin ? "dashboard" : "myleads");

  return (
    <div className="min-h-screen bg-[#0B0F14]">
      <header className="sticky top-0 z-40 bg-[#0B0F14]/90 backdrop-blur-xl border-b border-[#1E2D3D] px-4 sm:px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#25D366] flex items-center justify-center relative">
            <MessageCircle className="w-5 h-5 text-[#0B0F14]" strokeWidth={2.5} />
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-[#4ADE80] rounded-full pulse-dot border border-[#0B0F14]" />
          </div>
          <div>
            <span className="font-heading text-lg font-extrabold text-slate-50">LeadFlow.WA</span>
            <span className="hidden sm:inline text-xs text-slate-500 ml-2 font-mono-lf">Envolvía Global</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <p className="text-sm text-slate-200 font-medium">{user?.name}</p>
            <p className="text-xs text-[#25D366] font-mono-lf uppercase">{isAdmin ? "Admin" : "Vendedor"}</p>
          </div>
          <Button data-testid="logout-button" onClick={logout} variant="ghost" size="icon" className="text-slate-400 hover:text-red-400 hover:bg-red-500/10">
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {isAdmin && (
        <nav className="border-b border-[#1E2D3D] px-4 sm:px-6 flex gap-1 overflow-x-auto bg-[#080C10]">
          {ADMIN_TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  tab === t.id ? "border-[#25D366] text-[#25D366]" : "border-transparent text-slate-400 hover:text-slate-200"
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </nav>
      )}

      <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
        {isAdmin ? (
          <>
            {tab === "dashboard" && <AdminDashboard />}
            {tab === "leads" && <LeadsTable isAdmin />}
            {tab === "sellers" && <SellersManager />}
            {tab === "settings" && <SettingsPanel />}
          </>
        ) : (
          <SellerView />
        )}
      </main>
    </div>
  );
}
